import { useMemo } from 'react';
import { styled } from '@mui/material';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useServiceStatusInfo } from '../../hooks/useServiceStatusInfo';
import { columns } from './columns';
import { useGetWizerConnectorStatus } from '../../hooks/useGetWizerConnectorStatus';
import { useIsEligibleForFreeCtaas } from '../../hooks/useIsEligibleForFreeCtaas';
import AndromedaTable from '../../../../components/tables/tanstack/AndromedaTable';
import { RESILIENCE_SERVICES_TABLE_ID } from '../../../../components/tables/table_constants';
import { ServiceStatus } from '../../types';
import { FEATURE_FLAG_KEYS } from '../../../../config/growthbook';
import { useAgreements } from '../../features/self-serve/hooks/useAgreements';
import { isNonSelfServeProductCode } from '../../utils/productCodes';

export const ServiceTable = () => {
  const {
    data: serviceStatusInfo = [],
    isLoading,
    refetch,
  } = useServiceStatusInfo();
  const wizerConnectorStatus = useGetWizerConnectorStatus();
  const isEligibleForFreeCtaas = useIsEligibleForFreeCtaas();
  const crsEnableSelfServe = useFeatureIsOn(
    FEATURE_FLAG_KEYS.CRS_ENABLE_SELF_SERVE
  );
  const { data: agreements = [] } = useAgreements();

  const filteredServiceStatusInfo = useMemo(() => {
    if (!crsEnableSelfServe) return serviceStatusInfo;

    return serviceStatusInfo.filter((info) => {
      if (isNonSelfServeProductCode(info.product.code)) {
        return true;
      }

      if (info.status !== ServiceStatus.RequestReceived) {
        return true;
      }

      return false;
    });
  }, [serviceStatusInfo, crsEnableSelfServe]);

  return (
    <TableWrapper>
      <AndromedaTable
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        columns={columns as any}
        data={{
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          content: filteredServiceStatusInfo,
          totalPages: filteredServiceStatusInfo.length > 0 ? 1 : 0,
        }}
        isFetching={isLoading}
        refetch={refetch}
        getRowId={(row) => row.product.id}
        tableId={RESILIENCE_SERVICES_TABLE_ID}
        showHeader
        showFooter
        meta={{
          isEligibleForFreeCtaas,
          wizerConnectorStatus,
          agreements,
        }}
        skeleton={{ rowCount: 4 }}
        noRecordsMessage="No service found"
      >
        <div />
      </AndromedaTable>
    </TableWrapper>
  );
};

const TABLE_MIN_HEIGHT = '300px';

const TableWrapper = styled('div')(() => ({
  '& .MuiPaper-root > div': {
    height: 'auto',
    minHeight: TABLE_MIN_HEIGHT,
  },
}));
